import React, { Component } from 'react';
import { withRouteData, withSiteData } from 'react-static';
import PropTypes from 'prop-types';
import Qs from 'qs';

import giftupInit from '../../../../lib/giftup';
import { loadFbPixel } from '../../../analytics';

const CHECKOUT_PARAM_ATTRIBUTES = {
  promoCode: 'data-promo-code',
  purchaserEmail: 'data-purchaser-email',
  recipientEmail: 'data-recipient-email',
};

const CHECKOUT_STEPS = ['SelectItem', 'EnterDetails', 'Payment', 'OrderPlaced'];
const CHECKOUT_STEP_LABELS = {
  SelectItem: 'Gift Card Selection',
  EnterDetails: 'Details',
  Payment: 'Payment',
};

// giftup() keeps every callback it is given, so the conversion and page
// handlers are registered once and report through the most recently mounted checkout.
let trackConversion = null;
let trackCheckoutStep = null;

class GiftUp extends Component {
  state = {
    trackingData: {
      url: null,
      medium: null,
      source: null,
      campaign: null,
    },
  };

  constructor(props) {
    super();
    this.locale = props.locale;
    this.giftUpRef = React.createRef();

    const { content } = props;
    const isUs = this.locale === 'en-US';

    this.giftUpId = isUs ? content.en_us_id : content.default_id;
    this.giftUpGroupId = isUs ? null : content.default_groupId;
    this.giftUpProductId = (isUs && content.en_us_productId) || content.default_productId;
    this.giftUpWhoFor = content.whoFor;
    this.giftUpHideArtwork = content.hideArtwork;
    this.checkoutStep = this.giftUpProductId ? 'EnterDetails' : 'SelectItem';
  }

  componentDidMount() {
    const { search } = this.props.history.location;
    const queryParams = Qs.parse(search.slice(1));
    const trackingData = {
      ...this.state.trackingData,
      ...this.getTrackingData(queryParams),
    };
    // eslint-disable-next-line
    this.setState({ trackingData });

    this.applyCheckoutParams(queryParams);
    this.loadGiftUp();
    loadFbPixel();

    setTimeout(() => {
      this.observer = new ResizeObserver(this.props.setGiftUpHeight);
      if (this.giftUpRef.current) {
        this.observer.observe(this.giftUpRef.current);
      }
    }, 3000);
  }

  componentWillUnmount() {
    if (this.giftUpRef.current && this.observer) {
      this.observer.unobserve(this.giftUpRef.current);
    }
  }

  getTrackingData = utmParams => {
    return {
      url: window.location.href.split('?')[0] || null,
      medium: utmParams.utm_medium || null,
      source: utmParams.utm_source || null,
      campaign: utmParams.utm_campaign || null,
    };
  };

  render() {
    if (!this.giftUpId) {
      return null;
    }

    const props = {};
    if (this.giftUpGroupId) {
      props['data-group-id'] = this.giftUpGroupId;
    }
    if (this.giftUpProductId) {
      props['data-product-id'] = this.giftUpProductId;
    }
    if (this.giftUpWhoFor) {
      props['data-who-for'] = this.giftUpWhoFor;
    }
    if (this.giftUpHideArtwork) {
      props['data-hide-artwork'] = 'true';
    }

    return (
      <div
        ref={this.giftUpRef}
        className="gift-up-target"
        data-site-id={this.giftUpId}
        data-platform="Other"
        data-language={this.locale}
        data-referrer={this.props.pathWithoutBasename}
        {...props}
        id="gift-up"
      />
    );
  }

  applyCheckoutParams(queryParams) {
    const giftUpNode = this.giftUpRef.current;
    if (!giftUpNode) {
      return;
    }

    const params = { ...queryParams };
    const gift = this.giftUpWhoFor === 'someoneelse' || this.giftUpWhoFor === 'onlysomeoneelse';
    if (gift && typeof params.recipientEmail === 'string' && params.recipientEmail) {
      params.purchaserEmail = params.recipientEmail;
      delete params.recipientEmail;
    }

    Object.keys(CHECKOUT_PARAM_ATTRIBUTES).forEach(param => {
      const value = params[param];
      if (typeof value === 'string' && value) {
        giftUpNode.setAttribute(CHECKOUT_PARAM_ATTRIBUTES[param], value);
      }
    });
  }

  loadGiftUp() {
    giftupInit();
    // https://help.giftupapp.com/article/136-conversion-tracking-via-javascript
    if (!trackConversion) {
      giftup('conversion', data => trackConversion(data));
    }
    trackConversion = ({ revenue, currency, giftCards }) => {
      // In current flow user can purchase only one gift card at a time
      const voucherCode = giftCards[0].code;
      analytics.track('Purchased Gift Card', {
        ...this.state.trackingData,
        revenue,
        currency,
        voucherCode,
      });
    };

    if (!trackCheckoutStep) {
      giftup('page', page => trackCheckoutStep(page));
    }
    trackCheckoutStep = this.trackCheckoutStep;
  }

  trackCheckoutStep = page => {
    const previous = this.checkoutStep;
    this.checkoutStep = page;

    const label = CHECKOUT_STEP_LABELS[page];
    if (!label || page === previous) {
      return;
    }

    const back = CHECKOUT_STEPS.indexOf(page) < CHECKOUT_STEPS.indexOf(previous);
    analytics.track('Clicked CTA Button', {
      title: `${back ? 'Back to' : 'Continue to'} ${label}`,
      from: this.props.ctaFrom || 'Gift Card Checkout',
    });
  };
}

GiftUp.propTypes = {
  locale: PropTypes.string.isRequired,
  history: PropTypes.object,
  baseUrl: PropTypes.string,
  setGiftUpHeight: PropTypes.func.isRequired,
  ctaFrom: PropTypes.string,
};

export default withSiteData(withRouteData(GiftUp));



// WEBPACK FOOTER //
// /tmp/build_83f21ab6/src/components/main/giftCard/GiftUp.js